import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useMenuConfig } from '../hooks/useMenuConfig';
import { Bell, Bot, ChevronDown, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

const ResponsiveTopHeader = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { user, businesses, logout } = useAuth();
  const { activeBusiness, switchBusiness } = useBusiness();
  const { isOwner, isStaff } = usePermissions();
  const { planName } = useMenuConfig();

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="
      h-14 flex items-center justify-between px-4 sm:px-6 flex-shrink-0
      bg-gradient-to-b from-white to-slate-50/80
      backdrop-blur-md
      relative
    ">
      {/* Inner glass highlight */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent pointer-events-none" />
      {/* Subtle top glow line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
      
      {/* Left: Hamburger + Title */}
      <div className="flex items-center gap-3">
        {/* Hamburger Menu - Mobile Only */}
        <button
          onClick={onMenuClick}
          className="p-2 hover:bg-white/80 rounded-lg transition-all lg:hidden"
        >
          <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        </button>
        
        <h2 className="text-[14px] sm:text-[15px] font-medium text-slate-700 truncate tracking-tight">{activeBusiness?.name || 'Business'}</h2>
      </div>

      {/* Right: Business Selector, Plan Badge, Notifications, AI, User Menu */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Business Selector - Hide on small mobile */}
        {businesses.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 bg-white/70 border border-slate-200/60 rounded-lg hover:bg-white transition-all text-[12px]">
              <span className="font-medium text-slate-600 truncate max-w-[120px] md:max-w-[200px]">
                {activeBusiness?.name || 'Select Business'}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[11px] text-slate-500">Switch Business</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {businesses.map((business) => (
                <DropdownMenuItem
                  key={business.id}
                  onClick={() => switchBusiness(business.id)}
                  className="cursor-pointer text-[13px]"
                >
                  <div>
                    <div className="font-medium text-slate-700">{business.name}</div>
                    {business.phone && (
                      <div className="text-[11px] text-slate-400">{business.phone}</div>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Plan Badge - Premium styled, hide on mobile */}
        <div className="hidden md:flex relative px-2.5 py-1 bg-gradient-to-r from-violet-100/80 to-purple-100/80 border border-violet-200/50 rounded-md shadow-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
          <span className="relative text-[10px] font-medium text-violet-700 tracking-wide">{planName || 'Basic Plan'}</span>
        </div>

        {/* Notifications */}
        <button className="relative p-2 hover:bg-white/80 rounded-lg transition-all">
          <Bell className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
        </button>

        {/* AI Assistant - Hide on small mobile */}
        <button className="hidden sm:block p-2 hover:bg-white/80 rounded-lg transition-all">
          <Bot className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
        </button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2" data-testid="user-menu-trigger">
            <Avatar className="w-7 h-7 sm:w-8 sm:h-8 ring-2 ring-white/80 shadow-sm">
              <AvatarFallback className="bg-slate-600 text-white text-[10px] sm:text-[11px] font-medium">
                {getInitials(user?.name || 'U')}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div>
                <div className="font-medium text-slate-700 text-[13px]">{user?.name}</div>
                <div className="text-[11px] text-slate-400">
                  {user?.email || user?.phone}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-[13px] cursor-pointer"
              onClick={() => {
                const bId = businessId || activeBusiness?.id;
                if (bId) {
                  if (isStaff) {
                    navigate(`/business/${bId}/staff/my-profile`);
                  } else {
                    navigate(`/business/${bId}/settings`);
                  }
                }
              }}
            >
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-[13px] cursor-pointer"
              onClick={() => {
                const bId = businessId || activeBusiness?.id;
                if (bId) {
                  navigate(`/business/${bId}/settings`);
                }
              }}
            >
              Account Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => {
                logout();
                navigate('/login');
              }} 
              className="text-rose-600 cursor-pointer text-[13px]"
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default ResponsiveTopHeader;